import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { fetcher, RESUME_URL } from "@/lib/api";
import { PORTRAIT_SECONDARY } from "@/lib/assets";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { getIcon } from "@/components/iconMap";

export default function About() {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: () => fetcher("/site") });
  const profile = site?.profile;
  const strengths = site?.strengths || [];
  const portrait = PORTRAIT_SECONDARY;

  return (
    <div data-testid="about-page" className="pt-36 lg:pt-44 pb-24 lg:pb-32">
      <Seo
        title="About"
        description="About Nishant Gathwal — a UX designer in New Delhi with 8+ years across travel, edtech, and HR & enterprise software, educated at IIT Hyderabad and NSIT."
        path="/about"
        image={PORTRAIT_SECONDARY}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="text-xs tracking-[0.2em] uppercase text-ash mb-5">About</p>
          <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream text-balance max-w-4xl">
            I design simple, measurable experiences for complex software.
          </h1>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 space-y-6">
            {(profile?.bio || []).map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-cream/85 text-lg leading-relaxed">{p}</p>
              </Reveal>
            ))}

            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-4 pt-4">
                <a
                  href={RESUME_URL}
                  data-testid="about-resume-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-cream text-ink font-medium px-7 py-3.5 hover:bg-white transition-colors"
                >
                  <Download size={17} /> Download resume
                </a>
                <Link
                  to="/contact"
                  data-testid="about-contact-button"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 text-cream font-medium px-7 py-3.5 hover:bg-white/10 transition-colors"
                >
                  Get in touch <ArrowUpRight size={17} />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 bg-white/5">
              <img src={portrait} alt="Nishant Gathwal" className="h-full w-full object-cover object-top" />
            </div>
          </Reveal>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {(profile?.stats || []).map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div data-testid={`about-stat-${i}`} className="bg-ink p-8">
                <p className="font-heading font-light text-4xl lg:text-5xl tracking-tight text-cream">{s.value}</p>
                <p className="mt-2 text-ash text-sm">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Approach */}
        <div className="mt-24">
          <Reveal>
            <h2 className="font-heading font-light text-3xl lg:text-4xl tracking-tight text-cream mb-12">
              How I work
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {strengths.map((s, i) => {
              const Icon = getIcon(s.icon);
              return (
                <Reveal key={s.title} delay={i * 0.06}>
                  <div className="h-full bg-ink p-8 lg:p-10">
                    <Icon size={22} className="text-cream mb-6" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl text-cream mb-3">{s.title}</h3>
                    <p className="text-ash leading-relaxed">{s.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
